// apps/web/src/lib/tenant-resolver.ts — Phase 7 plan 07-06 (D-07-15 + W2 fix).
//
// Web-side tenant slug extraction. Two modes:
//   - Dev (NODE_ENV=development): read /t/<slug>/ from the URL path so
//     contributors can test tenant-scoped pages without editing
//     /etc/hosts.
//   - Prod: read <slug>.<baseDomain> from the hostname.
//
// `useTenantSlug()` is a React hook backed by context; it re-reads
// `window.location` on every render (cheap; one DOM property read).
// The context provider lives at the app root in main.tsx and wraps
// the public signup / confirm / verify / onboarding routes.
//
// The dev banner string is exported separately so a future UI
// surface (e.g. a `<DevBanner/>` component) can render it without
// re-importing i18n keys.
//
// Phase 8 plan 08-01 (D-08-01): the `__BASE_DOMAIN__` ambient
// constant is injected by Vite `define` at build time
// (apps/web/vite.config.ts). It replaces the previous hardcoded
// `gastio.app` apex so the deployment domain can be swapped
// without source edits.

import { createContext, useContext, useMemo, type ReactNode } from "react";

// Phase 8 plan 08-01 (D-08-01): ambient declaration for the
// build-time `define` constant. Vite substitutes the value as a
// JSON.stringify-wrapped string literal so the source can treat it
// as a plain string without imports.
// See apps/web/vite.config.ts (define block) + Pitfall 7 in
// 08-RESEARCH.md (JSON.stringify is mandatory).
declare const __BASE_DOMAIN__: string;

// Phase 7 D-07-15: tenant-path regex used by both the Vite middleware
// (apps/web/vite.config.ts) and this resolver. Keep them in sync.
const TENANT_PATH_RE = /^\/t\/([a-z0-9-]{3,40})(\/|$)/;

// Phase 7 D-07-15: subdomain regex for production routing. The base
// domain must contain at least one dot (e.g. "gastio.store").
const SUBDOMAIN_RE = /^([a-z0-9-]{3,40})\.([^.]+\.[a-z]{2,})$/;

export function resolveTenantSlug(
  hostname: string,
  path: string,
): string | null {
  // Dev path-based mode — overrides everything else (D-07-15).
  const pathMatch = path.match(TENANT_PATH_RE);
  if (pathMatch) return pathMatch[1] ?? null;
  // Production: parse the subdomain from the hostname.
  const subMatch = hostname.match(SUBDOMAIN_RE);
  if (!subMatch) return null;
  const slug = subMatch[1];
  // The bare domain has no subdomain → null. Bare hostname
  // (localhost) does not match the regex → null.
  return slug ?? null;
}

export function getCurrentTenantSlug(): string | null {
  if (typeof window === "undefined") return null;
  return resolveTenantSlug(window.location.hostname, window.location.pathname);
}

// Phase 7 D-07-15: derive the `<BrowserRouter basename>` prop from the
// current `window.location.pathname` so a URL like
// `/t/ristorante-esempio/signup` resolves internally to `/signup` —
// React Router matches the route that already exists (without the
// `/t/<slug>/` dev prefix) and `<Link to="/foo">` re-emits the full
// prefixed URL on outbound clicks. In production (no `/t/<slug>/`
// prefix in the URL) this returns `/`, leaving the router unchanged.
//
// The path-rewrite middleware in apps/web/vite.config.ts rewrites
// `req.url` server-side but cannot change the browser's
// `window.location.pathname` — `basename` is the client-side mirror
// that bridges the two views.
export function getTenantBasename(): string {
  if (typeof window === "undefined") return "/";
  const path = window.location.pathname;
  // Mirror the regex used by the path-rewrite middleware; keep them
  // in sync.
  const m = path.match(/^\/t\/([a-z0-9-]{3,40})(?=\/|$)/);
  if (!m) return "/";
  // m[0] is `/t/<slug>` (the consumed match has no trailing slash).
  return m[0];
}

// Dev-mode banner string (D-07-15). The current slug is interpolated
// at call time so the banner reads "Running in path-based dev mode —
// in prod this would be acme-bistro.<BASE_DOMAIN>". The apex comes
// from the build-time `__BASE_DOMAIN__` constant so the banner tracks
// the deployment domain automatically (Phase 8 plan 08-01).
export function devModeBanner(currentSlug: string | null): string {
  const slugFragment = currentSlug
    ? ` — in prod this would be ${currentSlug}.${__BASE_DOMAIN__}`
    : "";
  return `Running in path-based dev mode${slugFragment}`;
}

// ===== React context =====

const TenantSlugContext = createContext<string | null>(null);

export interface TenantSlugProviderProps {
  children: ReactNode;
}

export function TenantSlugProvider(props: TenantSlugProviderProps): ReactNode {
  const value = useMemo(() => getCurrentTenantSlug(), []);
  return (
    <TenantSlugContext.Provider value={value}>
      {props.children}
    </TenantSlugContext.Provider>
  );
}

export function useTenantSlug(): string | null {
  return useContext(TenantSlugContext);
}
